import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import {
    RULE_ARITHMETIC_FUNCTION_TYPES,
    RULE_STRING_FUNCTION_TYPES,
    RULE_SEGMENTATION_VALUE_TYPES,
} from 'yeself-common/enum/RuleEnum';

import SelectField from '../../components/SelectField';
import PlainStringField from '../../components/PlainStringField';

import { SegmentationValueRuleValues } from '../types';

type SegmentationValueRuleFieldProps = {
    value: SegmentationValueRuleValues;
    onChange: (value: SegmentationValueRuleValues) => void;
};

const SegmentationValueRuleField = ({
    value,
    onChange,
}: SegmentationValueRuleFieldProps): React.ReactElement => {
    const [levelValue, setLevelValue] = useState({
        level0: value.level?.[0]?.data.value || null,
        level1: value.level?.[1]?.data.key || null,
        level2: value.level?.[2]?.data.key || null,
        level3: value.level?.[3]?.data.value || null,
    });

    const handleOnChange = useCallback(
        (index: number) => (newValue) => {
            const newLevelValue = { ...levelValue, [`level${index}`]: newValue };

            const level: SegmentationValueRuleFieldProps['value']['level'] = [
                newLevelValue.level0 ? { data: { value: newLevelValue.level0 } } : undefined,
                newLevelValue.level1 ? { data: { key: newLevelValue.level1 } } : undefined,
                newLevelValue.level2 ? { data: { key: newLevelValue.level2 } } : undefined,
                newLevelValue.level3 ? { data: { value: newLevelValue.level3 } } : undefined,
            ];

            setLevelValue(newLevelValue);
            onChange({ ...value, level });
        },
        [levelValue, onChange, value]
    );

    return (
        <>
            <PlainStringField value={levelValue.level0 || ''} onChange={handleOnChange(0)} />
            {levelValue.level0 ? (
                <SelectField
                    autoWidth
                    options={[
                        {
                            label: <FormattedMessage id="Rules.level.segmentationType.date" />,
                            value: RULE_SEGMENTATION_VALUE_TYPES.DATE,
                        },
                        {
                            label: <FormattedMessage id="Rules.level.segmentationType.text" />,
                            value: RULE_SEGMENTATION_VALUE_TYPES.TEXT,
                        },
                        {
                            label: <FormattedMessage id="Rules.level.segmentationType.number" />,
                            value: RULE_SEGMENTATION_VALUE_TYPES.NUMBER,
                        },
                        {
                            label: <FormattedMessage id="Rules.level.segmentationType.list" />,
                            value: RULE_SEGMENTATION_VALUE_TYPES.LIST,
                        },
                    ]}
                    value={levelValue.level1 || ''}
                    onChange={handleOnChange(1)}
                />
            ) : null}
            {levelValue.level1 ? (
                <SelectField
                    autoWidth
                    options={[
                        {
                            label: <FormattedMessage id="Rules.level.eq" />,
                            value: RULE_ARITHMETIC_FUNCTION_TYPES.EQ,
                        },
                        {
                            label: <FormattedMessage id="Rules.level.gt" />,
                            value: RULE_ARITHMETIC_FUNCTION_TYPES.GT,
                        },
                        {
                            label: <FormattedMessage id="Rules.level.lt" />,
                            value: RULE_ARITHMETIC_FUNCTION_TYPES.LT,
                        },
                        {
                            label: <FormattedMessage id="Rules.level.mp" />,
                            value: RULE_STRING_FUNCTION_TYPES.MATCH_PATTERN,
                        },
                        {
                            label: <FormattedMessage id="Rules.level.one_of_cs" />,
                            value: RULE_ARITHMETIC_FUNCTION_TYPES.ONE_OF,
                        },
                    ]}
                    value={levelValue.level2 || ''}
                    onChange={handleOnChange(2)}
                />
            ) : null}
            {levelValue.level2 ? (
                <PlainStringField value={levelValue.level3 || ''} onChange={handleOnChange(3)} />
            ) : null}
        </>
    );
};

export default SegmentationValueRuleField;
